import { AccordionDetails, Stack, Typography } from '@mui/material';
import { type ReactElement, useCallback, useMemo } from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import {
  FlatAccordion,
  FlatAccordionSummary,
} from 'src/components/FlatAccordion/flat-accordion';
import type { ConversationAttachment } from 'src/graphql/entities/conversation';
import {
  useDeleteConversationAttachment,
  useMoveAttachmentToSiteFiles,
} from 'src/graphql/entities/files-upload';
import {
  type AttachmentRowModel,
  mapConversationAttachmentRows,
} from 'src/pages/Conversations/SidePanel/ConversationDetails/Attachments/attachment-utils';
import { ConversationAttachmentRow } from 'src/pages/Conversations/SidePanel/ConversationDetails/Attachments/conversation-attachment-row';
import { useConversationAttachmentsVisibility } from 'src/pages/Conversations/SidePanel/ConversationDetails/Attachments/use-conversation-attachments-visibility';
import { SectionHeader } from 'src/pages/Conversations/SidePanel/section-header';
// From its own module, not the `src/utils/hooks` barrel. This hook reaches the `src/components`
// barrel (via ConfirmationModal), which re-enters the entity gql modules and breaks fragment
// interpolation under vitest ("Unexpected Name undefined"). Importing the narrow path lets the
// suites for this component mock just this hook instead of the whole barrel.
import { useConfirmationModal } from 'src/utils/hooks/use-confirmation-modal';

interface ConversationAttachmentsProps {
  attachments: ConversationAttachment[];
  conversationId: string;
  siteAddressId?: string;
  userId?: string;
}

export const ConversationAttachments = ({
  attachments,
  conversationId,
  siteAddressId,
  userId,
}: ConversationAttachmentsProps): ReactElement | null => {
  const { changeConversationAttachmentsVisibility, currentConversationAttachmentsVisibility } =
    useConversationAttachmentsVisibility();
  const { moveToSiteFiles } = useMoveAttachmentToSiteFiles();
  const { deleteAttachment, canDeleteAttachments } = useDeleteConversationAttachment();
  const { confirm } = useConfirmationModal();
  const canAddToSiteFiles = Boolean(siteAddressId);

  const confirmAndDeleteAttachment = useCallback(
    async (attachment: AttachmentRowModel) => {
      const confirmed = await confirm({
        body: `Please confirm you want to delete "${attachment.fileName}".\nThe file will be permanently removed and cannot be restored.`,
        variant: 'delete',
      });

      if (confirmed) {
        await deleteAttachment(attachment.id);
      }
    },
    [confirm, deleteAttachment],
  );

  const rows = useMemo(() => mapConversationAttachmentRows(attachments), [attachments]);

  const onChangeConversationAttachmentsVisibility = useCallback(
    (_event: React.SyntheticEvent, isExpanded: boolean) => {
      changeConversationAttachmentsVisibility(isExpanded);
    },
    [changeConversationAttachmentsVisibility],
  );

  if (attachments.length === 0) {
    return null;
  }

  return (
    <FlatAccordion
      data-testid="conversation-attachments-section"
      disableGutters
      elevation={0}
      expanded={currentConversationAttachmentsVisibility}
      onChange={onChangeConversationAttachmentsVisibility}
    >
      <FlatAccordionSummary
        aria-controls={`conversation-attachments-content-${conversationId}`}
        data-testid="conversation-attachments-toggle"
        expandIcon={<DynamicIcon name="chevron-down" library="lucide" />}
        id={`conversation-attachments-header-${conversationId}`}
      >
        <Stack alignItems="center" direction="row" gap={0.5} width="100%">
          <SectionHeader title="Attachments" />
          <Typography
            color="text.secondary"
            data-testid="conversation-attachments-count"
            fontSize="0.75rem"
          >
            ({rows.length})
          </Typography>
        </Stack>
      </FlatAccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <Stack data-testid="conversation-attachments-list">
          {rows.map((attachment) => (
            <ConversationAttachmentRow
              key={attachment.id}
              attachment={attachment}
              canDragToSiteFiles={canAddToSiteFiles}
              onAddToSiteFiles={
                canAddToSiteFiles
                  ? () =>
                      moveToSiteFiles({
                        attachment: attachment.siteFilePayload,
                        addressId: siteAddressId,
                        uploadedByUserId: userId,
                      })
                  : undefined
              }
              onDelete={
                canDeleteAttachments ? () => confirmAndDeleteAttachment(attachment) : undefined
              }
            />
          ))}
        </Stack>
      </AccordionDetails>
    </FlatAccordion>
  );
};
